/**
 * API Configuration
 * Centralized API settings for the application
 */

import { withUploadsToken } from '@/utils/uploadsAuth';

// Base URL for the backend API
// Uses relative URLs by default so Vite's dev proxy (/api → localhost:3002) handles routing.
// This ensures the app works via localhost:3000 AND proxied domains (e.g. hookhouse-pro.fmr.local).
// Set VITE_API_BASE_URL to an absolute URL only if the backend runs on a separate origin in production.
export const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

// API endpoints grouped by feature
export const API_ENDPOINTS = {
  // Drafts/Vault
  drafts: {
    list: `${API_BASE}/api/drafts`,
    titles: `${API_BASE}/api/drafts/titles`,
    create: `${API_BASE}/api/drafts`,
    update: (id: string | number) => `${API_BASE}/api/drafts/${id}`,
    delete: (id: string | number) => `${API_BASE}/api/drafts/${id}`,
    uploadMd: `${API_BASE}/api/drafts/upload-md`,
    uploadArt: (id: string | number) => `${API_BASE}/api/drafts/${id}/upload-art`,
    favorite: (id: string | number) => `${API_BASE}/api/drafts/${id}/favorite`,
  },

  // VocalForge
  vocalPacks: {
    list: `${API_BASE}/api/vocal-packs/list`,
    get: (key: string) => `${API_BASE}/api/vocal-packs/${encodeURIComponent(key)}`,
    create: `${API_BASE}/api/vocal-packs`,
    delete: `${API_BASE}/api/vocal-packs`,
  },

  // InstrumentForge
  instrumentPacks: {
    list: `${API_BASE}/api/instrument-packs/list`,
    get: (key: string) => `${API_BASE}/api/instrument-packs/${encodeURIComponent(key)}`,
    create: `${API_BASE}/api/instrument-packs`,
    delete: `${API_BASE}/api/instrument-packs`,
  },

  // SoloForge
  soloPacks: {
    list: `${API_BASE}/api/solo-packs/list`,
    get: (key: string) => `${API_BASE}/api/solo-packs/${encodeURIComponent(key)}`,
    create: `${API_BASE}/api/solo-packs`,
    delete: `${API_BASE}/api/solo-packs`,
  },

  // MixForge
  mixTemplates: {
    list: `${API_BASE}/api/mix-templates/list`,
    get: (id: string) => `${API_BASE}/api/mix-templates/${id}`,
    generate: `${API_BASE}/api/mix-templates/generate`,
    add: `${API_BASE}/api/mix-templates/add`,
    delete: `${API_BASE}/api/mix-templates`,
  },

  // GenreForge
  genreModules: {
    list: `${API_BASE}/api/genre-modules/list`,
    get: (slug: string) => `${API_BASE}/api/genre-modules/${encodeURIComponent(slug)}`,
    generate: `${API_BASE}/api/genre-modules/generate`,
    add: `${API_BASE}/api/genre-modules/add`,
    delete: `${API_BASE}/api/genre-modules`,
    checkDuplicate: `${API_BASE}/api/genre-modules/check-duplicate`,
  },

  // Health monitoring
  health: {
    providers: `${API_BASE}/api/health/providers`,
    system: `${API_BASE}/api/health/system`,
  },

  // ngrok Tunnel Manager
  ngrok: {
    start: `${API_BASE}/api/ngrok/start`,
    stop: `${API_BASE}/api/ngrok/stop`,
    status: `${API_BASE}/api/ngrok/status`,
    requests: `${API_BASE}/api/ngrok/requests`,
    config: `${API_BASE}/api/ngrok/config`,
  },

  // Database management
  database: {
    info: `${API_BASE}/api/database/info`,
    backup: `${API_BASE}/api/database/backup`,
    deleteBackup: (filename: string) => `${API_BASE}/api/database/backup/${encodeURIComponent(filename)}`,
    scheduler: `${API_BASE}/api/database/scheduler`,
  },

  // Suno API proxy
  suno: {
    billing: `${API_BASE}/api/suno/billing`,
    feed: `${API_BASE}/api/suno/feed`,
    generate: `${API_BASE}/api/suno/generate`,
  },

  // Generation Sessions
  sessions: {
    list: `${API_BASE}/api/sessions`,
    create: `${API_BASE}/api/sessions`,
    get: (id: string | number) => `${API_BASE}/api/sessions/${id}`,
    rate: (id: string | number) => `${API_BASE}/api/sessions/${id}/rating`,
    delete: (id: string | number) => `${API_BASE}/api/sessions/${id}`,
    insights: `${API_BASE}/api/sessions/insights`,
    effectiveness: `${API_BASE}/api/sessions/effectiveness`,
  },

  // Analytics
  analytics: {
    trends: `${API_BASE}/api/sessions/analytics/trends`,
    recipes: `${API_BASE}/api/sessions/analytics/recipes`,
    heatmap: `${API_BASE}/api/sessions/analytics/heatmap`,
  },

  // Personas
  personas: {
    list: `${API_BASE}/api/personas`,
    delete: (id: string | number) => `${API_BASE}/api/personas/${id}`,
  },

  // Cost Control
  costs: {
    log: `${API_BASE}/api/costs/log`,
    logBatch: `${API_BASE}/api/costs/log/batch`,
    today: `${API_BASE}/api/costs/today`,
    totals: `${API_BASE}/api/costs/totals`,
    logs: `${API_BASE}/api/costs/logs`,
    summary: `${API_BASE}/api/costs/summary`,
    actualStatus: `${API_BASE}/api/costs/actual/status`,
    actualSync: `${API_BASE}/api/costs/actual/sync`,
    actualDaily: `${API_BASE}/api/costs/actual/daily`,
  },

  // Variation Chains
  variations: {
    list: `${API_BASE}/api/variations`,
    create: `${API_BASE}/api/variations`,
    get: (id: string | number) => `${API_BASE}/api/variations/${id}`,
    delete: (id: string | number) => `${API_BASE}/api/variations/${id}`,
    nodes: (id: string | number) => `${API_BASE}/api/variations/${id}/nodes`,
    spawn: (id: string | number) => `${API_BASE}/api/variations/${id}/spawn`,
    rateNode: (chainId: string | number, nodeId: string | number) =>
      `${API_BASE}/api/variations/${chainId}/nodes/${nodeId}/rating`,
    updateNodeStatus: (chainId: string | number, nodeId: string | number) =>
      `${API_BASE}/api/variations/${chainId}/nodes/${nodeId}/status`,
    updateStatus: (id: string | number) => `${API_BASE}/api/variations/${id}/status`,
  },

  // MidiForge
  midiforge: {
    health: `${API_BASE}/api/midiforge/health`,
    forge: `${API_BASE}/api/midiforge/forge`,
    reforge: `${API_BASE}/api/midiforge/reforge`,
    jobs: `${API_BASE}/api/midiforge/jobs`,
    job: (id: string | number) => `${API_BASE}/api/midiforge/jobs/${id}`,
    jobStatus: (id: string | number) => `${API_BASE}/api/midiforge/jobs/${id}/status`,
    deleteJob: (id: string | number) => `${API_BASE}/api/midiforge/jobs/${id}`,
  },

  // MasterForge
  masterforge: {
    health: `${API_BASE}/api/masterforge/health`,
    master: `${API_BASE}/api/masterforge/master`,
    proposeChain: `${API_BASE}/api/masterforge/propose-chain`,
    masterFromMidiforge: `${API_BASE}/api/masterforge/master-from-midiforge`,
    remaster: `${API_BASE}/api/masterforge/remaster`,
    jobs: `${API_BASE}/api/masterforge/jobs`,
    job: (id: string | number) => `${API_BASE}/api/masterforge/jobs/${id}`,
    jobStatus: (id: string | number) => `${API_BASE}/api/masterforge/jobs/${id}/status`,
    deleteJob: (id: string | number) => `${API_BASE}/api/masterforge/jobs/${id}`,
    export: (id: string | number) => `${API_BASE}/api/masterforge/jobs/${id}/export`,
    separate: (id: string | number) => `${API_BASE}/api/masterforge/jobs/${id}/separate`,
    remix: (id: string | number) => `${API_BASE}/api/masterforge/jobs/${id}/remix`,
  },

  // FFmpeg Audio Processor
  ffmpeg: {
    health: `${API_BASE}/api/ffmpeg/health`,
    process: `${API_BASE}/api/ffmpeg/process`,
    jobs: `${API_BASE}/api/ffmpeg/jobs`,
    job: (id: string | number) => `${API_BASE}/api/ffmpeg/jobs/${id}`,
    jobStatus: (id: string | number) => `${API_BASE}/api/ffmpeg/jobs/${id}/status`,
    deleteJob: (id: string | number) => `${API_BASE}/api/ffmpeg/jobs/${id}`,
    sendToMasterforge: (id: string | number) => `${API_BASE}/api/ffmpeg/jobs/${id}/send-to-masterforge`,
  },

  // Storage Manager
  storage: {
    scan: `${API_BASE}/api/storage/scan`,
    cleanup: `${API_BASE}/api/storage/cleanup`,
    disk: `${API_BASE}/api/storage/disk`,
  },

  // Reference Tracks
  referenceTracks: {
    upload: `${API_BASE}/api/reference-tracks/upload`,
    delete: (filename: string) => `${API_BASE}/api/reference-tracks/${encodeURIComponent(filename)}`,
  },

  // Forge Generation History
  forgeGenerations: {
    list: `${API_BASE}/api/forge-generations/list`,
    get: (id: string) => `${API_BASE}/api/forge-generations/${id}`,
    create: `${API_BASE}/api/forge-generations`,
    rate: (id: string) => `${API_BASE}/api/forge-generations/${id}/rating`,
    delete: (id: string) => `${API_BASE}/api/forge-generations/${id}`,
  },

  // Critique Sessions
  critiqueSessions: {
    list: `${API_BASE}/api/critique-sessions/list`,
    get: (id: string) => `${API_BASE}/api/critique-sessions/${id}`,
    create: `${API_BASE}/api/critique-sessions`,
    delete: (id: string) => `${API_BASE}/api/critique-sessions/${id}`,
  },

  // Suno Clip History
  sunoClipHistory: {
    list: `${API_BASE}/api/suno-clip-history/list`,
    get: (id: string) => `${API_BASE}/api/suno-clip-history/${id}`,
    create: `${API_BASE}/api/suno-clip-history`,
    delete: (id: string) => `${API_BASE}/api/suno-clip-history/${id}`,
  },

  // Live Venue Templates
  liveVenueTemplates: {
    list: `${API_BASE}/api/live-venue-templates/list`,
    get: (id: string) => `${API_BASE}/api/live-venue-templates/${id}`,
    create: `${API_BASE}/api/live-venue-templates`,
    update: (id: string) => `${API_BASE}/api/live-venue-templates/${id}`,
    delete: (id: string) => `${API_BASE}/api/live-venue-templates/${id}`,
  },

  // Album Drafts
  albumDrafts: {
    list: `${API_BASE}/api/album-drafts/list`,
    get: (id: string) => `${API_BASE}/api/album-drafts/${id}`,
    create: `${API_BASE}/api/album-drafts`,
    update: (id: string) => `${API_BASE}/api/album-drafts/${id}`,
    delete: (id: string) => `${API_BASE}/api/album-drafts/${id}`,
  },

  // User Settings (encrypted API keys)
  settings: `${API_BASE}/api/settings`,

  // Lyric Style Library
  lyricStyles: {
    list: `${API_BASE}/api/lyric-styles/list`,
    get: (slug: string) => `${API_BASE}/api/lyric-styles/${slug}`,
  },

  // File serving (uploads) -- /uploads requires an authenticated request
  // (server/middleware.ts); native <img>/<audio>/<source>/<a href> can't
  // attach an Authorization header, so the access token rides along as a
  // query param instead. See src/utils/uploadsAuth.ts.
  files: {
    url: (path: string) => withUploadsToken(`${API_BASE}${path}`),
  },
} as const;
